import {
  IntervalJSON,
  FixedTimeRangeConfig,
} from '@highimpact-frontend/api-types';

/**
 * Defines whether the time period is before or after the reference date.
 */
export type Direction = 'before' | 'after';

/**
 * Defines the possible units of time to represent the period.
 */
export type TimeUnit =
  | 'millisecondes'
  | 'seconds'
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months'
  | 'years';

// -- type definitions --

export type TimeRangeUnion = IntervalJSON;

// -- helper functions --

/**
 * Parses an ISO 8601 string into a Date object. Throws error if invalid.
 */
function parseIsoString(value: string): Date {
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    throw new Error(`${value} is not a valid ISO 8601 datetime string.`);
  }
  return date;
}

/**
 * Adds a specific quantity of a time unit to a date, returning a new Date instance.
 */
function addUnit(date: Date, unit: TimeUnit, quantity: number): Date {
  const newDate = new Date(date.getTime());
  switch (unit) {
    case 'millisecondes':
      newDate.setUTCMilliseconds(newDate.getUTCMilliseconds() + quantity);
      break;
    case 'seconds':
      newDate.setUTCSeconds(newDate.getUTCSeconds() + quantity);
      break;
    case 'minutes':
      newDate.setUTCMinutes(newDate.getUTCMinutes() + quantity);
      break;
    case 'hours':
      newDate.setUTCHours(newDate.getUTCHours() + quantity);
      break;
    case 'days':
      newDate.setUTCDate(newDate.getUTCDate() + quantity);
      break;
    case 'weeks':
      newDate.setUTCDate(newDate.getUTCDate() + quantity * 7);
      break;
    case 'months':
      newDate.setUTCMonth(newDate.getUTCMonth() + quantity);
      break;
    case 'years':
      newDate.setUTCFullYear(newDate.getUTCFullYear() + quantity);
      break;
  }
  return newDate;
}

/**
 * Resets the date to the beginning of the specified time unit.
 */
function resetReferenceByUnit(date: Date, unit: TimeUnit): Date {
  const newDate = new Date(date.getTime());
  switch (unit) {
    case 'millisecondes':
      // no-op for milliseconds representation in native Date
      break;
    case 'seconds':
      newDate.setUTCMilliseconds(0);
      break;
    case 'minutes':
      newDate.setUTCSeconds(0, 0);
      break;
    case 'hours':
      newDate.setUTCMinutes(0, 0, 0);
      break;
    case 'days':
      newDate.setUTCHours(0, 0, 0, 0);
      break;
    case 'weeks': {
      newDate.setUTCHours(0, 0, 0, 0);
      const day = newDate.getUTCDay();
      const diff = day === 0 ? 6 : day - 1; // adjust to make monday the first day
      newDate.setUTCDate(newDate.getUTCDate() - diff);
      break;
    }
    case 'months':
      newDate.setUTCDate(1);
      newDate.setUTCHours(0, 0, 0, 0);
      break;
    case 'years':
      newDate.setUTCMonth(0, 1);
      newDate.setUTCHours(0, 0, 0, 0);
      break;
  }
  return newDate;
}

/**
 * Formats a Date object back to an ISO string alternative or local string equivalent.
 */
function formatDate(date: Date): string {
  return date.toISOString();
}

// -- main function --

/**
 * Pure function to calculate start and end dates based on a fixed reference date.
 */
export function calculateStartAndEndDate(
  timeRange: TimeRangeUnion,
  nowDate: Date
): [string, string] {
  if (timeRange.type === 'fixed_time_range') {
    // validation triggers during parsing
    parseIsoString(timeRange.startdate);
    parseIsoString(timeRange.enddate);
    return [timeRange.startdate, timeRange.enddate];
  }

  const baseDate =
    timeRange.relative_to === 'now'
      ? new Date(nowDate.getTime())
      : parseIsoString(timeRange.relative_to);

  const shift = timeRange.period_shift ?? 1;

  if (timeRange.type === 'relative_time_range') {
    if (timeRange.direction === 'before') {
      const deltaStart = timeRange.quantity * (shift + 1);
      const deltaEnd = timeRange.quantity * shift;

      const startDate = addUnit(baseDate, timeRange.unit, -deltaStart);
      const endDate = addUnit(baseDate, timeRange.unit, -deltaEnd);
      return [formatDate(startDate), formatDate(endDate)];
    } else {
      const deltaStart = timeRange.quantity * shift;
      const deltaEnd = timeRange.quantity * (shift + 1);

      const startDate = addUnit(baseDate, timeRange.unit, deltaStart);
      const endDate = addUnit(baseDate, timeRange.unit, deltaEnd);
      return [formatDate(startDate), formatDate(endDate)];
    }
  }

  if (timeRange.type === 'calendar_time_range') {
    if (timeRange.direction === 'before') {
      const cleanedBase = resetReferenceByUnit(baseDate, timeRange.unit);
      const startRef = addUnit(cleanedBase, timeRange.unit, 1);

      const deltaStart = timeRange.quantity * (shift + 1);
      const deltaEnd = timeRange.quantity * shift;

      const startDate = addUnit(startRef, timeRange.unit, -deltaStart);
      const endDate = addUnit(startRef, timeRange.unit, -deltaEnd);
      return [formatDate(startDate), formatDate(endDate)];
    } else {
      const cleanedBase = resetReferenceByUnit(baseDate, timeRange.unit);
      const deltaStart = timeRange.quantity * shift;
      const deltaEnd = timeRange.quantity * (shift + 1);

      const startDate = addUnit(cleanedBase, timeRange.unit, deltaStart);
      const endDate = addUnit(cleanedBase, timeRange.unit, deltaEnd);
      return [formatDate(startDate), formatDate(endDate)];
    }
  }

  throw new Error('TimeRange unknown');
}

/**
 * Main orchestration function containing the side-effect of fetching the current system time.
 */
export function getStartAndEndDate(
  timeRange: TimeRangeUnion
): [string, string] {
  return calculateStartAndEndDate(timeRange, new Date());
}

/**
 * Utility function to determine the span of a list of time ranges, returning the earliest start and latest end.
 */
export function getOverallTimeSpan(
  timeRanges: TimeRangeUnion[]
): [string, string] | null {
  let overallStart: Date | null = null;
  let overallEnd: Date | null = null;

  timeRanges.forEach((range) => {
    const [startStr, endStr] = getStartAndEndDate(range);
    const start = parseIsoString(startStr);
    const end = parseIsoString(endStr);

    if (!overallStart || start < overallStart) {
      overallStart = start;
    }
    if (!overallEnd || end > overallEnd) {
      overallEnd = end;
    }
  });

  if (!overallStart || !overallEnd) {
    return null;
  }

  return [formatDate(overallStart), formatDate(overallEnd)];
}

/**
 * Converts an interval of any type into a fixed time range interval.
 */
export function resolveIntervalToFixed(
  interval: IntervalJSON
): FixedTimeRangeConfig {
  if (interval.type === 'fixed_time_range') {
    return interval;
  }

  const [startdate, enddate] = getStartAndEndDate(interval);

  return {
    type: 'fixed_time_range',
    name: interval.name ?? null,
    startdate,
    enddate,
  };
}
